import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import LoadingScreen from './components/LoadingScreen';

// Guards e layout: pequenos e compartilhados por todas as rotas -> ficam no bundle inicial.
import MainLayout from './layouts/MainLayout';
import PrivateRoute from './routes/PrivateRoute';
import RoleRoute from './routes/RoleRoute';
import AndonRoute from './routes/AndonRoute';
import { ehAndonTv, ehSessaoTv, ROTA_ANDON_TV, rotaLiberadaNoQuiosque } from './utils/andon';
import { ROTA_TROCA_SENHA, sessaoComTrocaPendente, rotaLiberadaNaTroca } from './utils/trocaSenha';
import SoftwaresRoute from './routes/SoftwaresRoute';
import ExigeCoordenador from './components/ExigeCoordenador';

// Paginas: carregadas sob demanda (code splitting por rota). Cada uma vira um
// chunk separado, incluindo suas libs pesadas (jspdf, recharts, leaflet,
// fullcalendar, react-quill, react-pdf) e CSS, que so baixam quando a rota abre.
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Cadastro = lazy(() => import('./pages/Cadastro'));
const RecuperarSenha = lazy(() => import('./pages/RecuperarSenha'));
const RedefinirSenha = lazy(() => import('./pages/RedefinirSenha'));
const DefinirNovaSenha = lazy(() => import('./pages/DefinirNovaSenha'));
const Cursos = lazy(() => import('./pages/Cursos'));
const CursoDetalhe = lazy(() => import('./pages/CursoDetalhe'));
const Admin = lazy(() => import('./pages/Admin'));
const Certificados = lazy(() => import('./pages/Certificados'));
const Ranking = lazy(() => import('./pages/Ranking'));
const Perfil = lazy(() => import('./pages/Perfil'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Progresso = lazy(() => import('./pages/Progresso'));
const Biblioteca = lazy(() => import('./pages/Biblioteca'));
const BibliotecaPublica = lazy(() => import('./pages/BibliotecaPublica'));
const VerificarCertificado = lazy(() => import('./pages/VerificarCertificado'));
const BaixarSimmaq = lazy(() => import('./pages/BaixarSimmaq'));
const Assistente = lazy(() => import('./pages/Assistente'));
const CloudMqtt = lazy(() => import('./pages/CloudMqtt'));
const CloudDashboardPublico = lazy(() => import('./pages/CloudDashboardPublico'));
const Suporte = lazy(() => import('./pages/Suporte'));
const RealidadeAumentada = lazy(() => import('./pages/RealidadeAumentada'));
const Licencas = lazy(() => import('./pages/Licencas/Licencas'));
const Softwares = lazy(() => import('./pages/Softwares/Softwares'));
const Assinaturas = lazy(() => import('./pages/Assinaturas/Assinaturas'));
const SoftwareDetalhe = lazy(() => import('./pages/Softwares/SoftwareDetalhe'));

// Modulo de Tarefas (docs/tarefas/PLANO-TAREFAS.md).
const TarefasLayout = lazy(() => import('./pages/Tarefas/TarefasLayout'));
const MinhasTarefas = lazy(() => import('./pages/Tarefas/MinhasTarefas'));
const TarefasProjetos = lazy(() => import('./pages/Tarefas/Projetos'));
const TarefasProjetoDetalhe = lazy(() => import('./pages/Tarefas/ProjetoDetalhe'));
const TarefasRelatorio = lazy(() => import('./pages/Tarefas/Relatorio'));
const TarefasCronograma = lazy(() => import('./pages/Tarefas/Cronograma'));
const TarefasEquipes = lazy(() => import('./pages/Tarefas/Equipes'));
const TarefasJornadas = lazy(() => import('./pages/Tarefas/Jornadas'));
const TarefasFerias = lazy(() => import('./pages/Tarefas/Ferias'));
const TarefasMural = lazy(() => import('./pages/Tarefas/Mural'));

// Prestação de Contas (docs/prestacao-contas.md): colaborador e financeiro.
const MinhasPrestacoes = lazy(() => import('./pages/PrestacaoContas/MinhasPrestacoes'));
const PrestacaoDetalhe = lazy(() => import('./pages/PrestacaoContas/PrestacaoDetalhe'));
const PainelFinanceiro = lazy(() => import('./pages/PrestacaoContas/financeiro/PainelFinanceiro'));
const FinImportacoes = lazy(() => import('./pages/PrestacaoContas/financeiro/Importacoes'));
const FinImportacaoDetalhe = lazy(() => import('./pages/PrestacaoContas/financeiro/ImportacaoDetalhe'));
const FinRevisaoPrestacao = lazy(() => import('./pages/PrestacaoContas/financeiro/RevisaoPrestacao'));
const FinUberCorporativo = lazy(() => import('./pages/PrestacaoContas/financeiro/UberCorporativo'));

// Andon: painel da produção (modo Gestão no layout; modo TV em tela cheia).
const AndonPainel = lazy(() => import('./pages/Andon/AndonPainel'));
const AndonTV = lazy(() => import('./pages/Andon/AndonTV'));

const CadastroEscola = lazy(() => import('./pages/Admin/CadastroEscola'));
const CadastroCoordenador = lazy(() => import('./pages/Admin/CadastroCoordenador'));
const CadastroExxer = lazy(() => import('./pages/Admin/CadastroExxer'));
const CadastroTemporario = lazy(() => import('./pages/Admin/CadastroTemporario'));
const InformacoesAdmin = lazy(() => import('./pages/Admin/Informacoes'));
const UsuariosOnline = lazy(() => import('./pages/Admin/UsuariosOnline'));
const Auditoria = lazy(() => import('./pages/Admin/Auditoria'));
const ConsumoIA = lazy(() => import('./pages/Admin/ConsumoIA'));
const BibliotecaAdmin = lazy(() => import('./pages/Admin/BibliotecaAdmin'));
const VincularAlunosEscolas = lazy(() => import('./pages/Admin/VincularAlunosEscolas'));
const CadastroLote = lazy(() => import('./pages/Admin/CadastroLote'));

const CadastroAluno = lazy(() => import('./pages/Coordenador/CadastroAluno'));
const VincularAlunos = lazy(() => import('./pages/Coordenador/VincularAlunos'));
const VincularProfessores = lazy(() => import('./pages/Coordenador/VincularProfessores'));
const CadastroTurma = lazy(() => import('./pages/Coordenador/CadastroTurma'));
const CadastroProfessor = lazy(() => import('./pages/Coordenador/CadastroProfessor'));
const Alocacao = lazy(() => import('./pages/Coordenador/Alocacao'));
const Avisos = lazy(() => import('./pages/Coordenador/Avisos'));

const CadastroCurso = lazy(() => import('./pages/Professor/CadastroCurso'));
const ListarCursos = lazy(() => import('./pages/Professor/ListarCursos'));
const ForumDaTurma = lazy(() => import('./pages/Professor/ForumDaTurma'));
const ProvasProfessor = lazy(() => import('./pages/Professor/Provas'));
const VincularProvas = lazy(() => import('./pages/Professor/VincularProvas'));
const AvaliarProvas = lazy(() => import('./pages/Professor/AvaliarProvas'));
const AvaliarAluno = lazy(() => import('./pages/Professor/AvaliarAluno'));
const Presenca = lazy(() => import('./pages/Professor/Presenca'));

// Provas online do Simmaq 3DX (docs/provas-simmaq.md): professor e coordenação.
const ListaProvasSimmaq = lazy(() => import('./pages/ProvasSimmaq/ListaProvasSimmaq'));
const FormProvaSimmaq = lazy(() => import('./pages/ProvasSimmaq/FormProvaSimmaq'));
const ProvaSimmaq = lazy(() => import('./pages/ProvasSimmaq/ProvaSimmaq'));
const TentativaSimmaq = lazy(() => import('./pages/ProvasSimmaq/TentativaSimmaq'));

const MeusCursosAluno = lazy(() => import('./pages/Aluno/MeusCursosAluno'));
const CalendarioTurmas = lazy(() => import('./pages/Aluno/CalendarioTurmas'));
const ProvasAluno = lazy(() => import('./pages/Aluno/ProvasAluno'));
const ResultadoProvasAlunos = lazy(() => import('./pages/Aluno/ResultadoProvasAlunos'));

const ChatPrivado = lazy(() => import('./pages/Chat/ChatPrivado'));
const SelecionarContatoChat = lazy(() => import('./pages/Chat/SelecionarContatoChat'));

const AppRoutes = () => {
  const { usuario, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <LoadingScreen />;

  // Quiosque preso: usuário Andon 'tv' (papel exxer) só usa /andon/tv. Guarda
  // central, antes das rotas: qualquer outro endereço volta para a TV.
  if (ehSessaoTv(usuario) && !rotaLiberadaNoQuiosque(pathname)) {
    return <Navigate to={ROTA_ANDON_TV} replace />;
  }

  // "Exigir troca de senha no próximo acesso": a sessão com a marca troca_senha
  // só usa a tela "Defina sua nova senha" (a API também recusa o resto, 403).
  if (sessaoComTrocaPendente(usuario) && !rotaLiberadaNaTroca(pathname)) {
    return <Navigate to={ROTA_TROCA_SENHA} replace />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Públicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/cadastro" element={<Cadastro />} />
        <Route path="/recuperar-senha" element={<RecuperarSenha />} />
        <Route path="/redefinir-senha/:token" element={<RedefinirSenha />} />
        <Route path="/biblioteca/publica/:link" element={<BibliotecaPublica />} />
        <Route path="/cloud/publico/:token" element={<CloudDashboardPublico />} />
        {/* Verificacao de certificados: aberta a qualquer visitante, com ou sem codigo na URL (QR Code). */}
        <Route path="/certificados/verificar" element={<VerificarCertificado />} />
        <Route path="/certificados/verificar/:codigo" element={<VerificarCertificado />} />
        {/* Realidade aumentada: aberta a qualquer visitante, em geral pelo QR Code (augmented-reality/README.md). */}
        <Route path="/ar/demonstracao/:equipamento" element={<RealidadeAumentada />} />
        {/* Download do Simmaq 3DX (Simmaq Essencial grátis): aberto a qualquer visitante, logado ou não. */}
        <Route path="/baixar/simmaq-3dx" element={<BaixarSimmaq />} />
        {/* Suporte é público para abertura e acesso protegido por senha ao chamado. */}
        {!usuario && <Route path="/suporte" element={<Suporte />} />}
        {!usuario && <Route path="/suporte/:codigo" element={<Suporte />} />}

        {/* Protegidas */}
        <Route element={<PrivateRoute />}>
          {/* Andon modo TV: quiosque fora do MainLayout (sem menu); Navbar e Footer
              também se escondem nesta rota. */}
          <Route element={<AndonRoute />}>
            <Route path="/andon/tv" element={<AndonTV />} />
          </Route>
          {/* Troca de senha exigida pelo gestor: fora do MainLayout (sem menu). */}
          <Route path={ROTA_TROCA_SENHA} element={sessaoComTrocaPendente(usuario) ? <DefinirNovaSenha /> : <Navigate to="/home" replace />} />
          <Route element={<MainLayout />}>
            <Route path="/home" element={<Home />} />
            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador', 'professor']} moduleKey="cursos" gestaoEscolar />}>
              <Route path="/cursos" element={<Cursos />} />
              <Route path="/curso/:id" element={<CursoDetalhe />} />
            </Route>
            <Route path="/perfil" element={<Perfil />} />
            {/* Softwares Exxer: a API decide quem vê (config/softwares.js e routes/softwares.js). */}
            <Route element={<SoftwaresRoute />}>
              <Route path="/softwares" element={<Softwares />} />
              <Route path="/softwares/downloads/:slug" element={<SoftwareDetalhe />} />
              <Route path="/softwares/:slug" element={<SoftwareDetalhe />} />
            </Route>
            <Route path="/suporte" element={<Suporte />} />
            <Route path="/suporte/:codigo" element={<Suporte />} />
            {/* Só professor e aluno: todos os endpoints /api/forum* usam
                checkRole(['professor', 'aluno']). Sem esta guarda, coordenador, admin,
                exxer e temporário abriam a tela e tomavam 403 em cada chamada. */}
            <Route element={<RoleRoute allowed={['professor', 'aluno']} moduleKey="cursos" />}>
              <Route path="/forum/:turmaId" element={<ForumDaTurma />} />
            </Route>
            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador', 'professor', 'aluno']} moduleKey="cursos" gestaoEscolar />}>
              <Route path="/certificados" element={<Certificados />} />
              <Route path="/ranking" element={<Ranking />} />
            </Route>
            <Route element={<RoleRoute allowed={['professor', 'aluno']} moduleKey="cursos" />}>
              <Route path="/chat" element={<SelecionarContatoChat />} />
              <Route path="/chat-privado/:destinoId" element={<ChatPrivado />} />
            </Route>
            <Route element={<RoleRoute allowed={['coordenador', 'professor', 'aluno']} moduleKey="cursos" />}>
              <Route path="/relatorios" element={<Relatorios />} />
            </Route>
            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador', 'professor', 'aluno']} moduleKey="cursos" gestaoEscolar />}>
              <Route path="/progresso" element={<Progresso />} />
            </Route>
            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador', 'professor', 'aluno', 'temporario']} moduleKey="biblioteca" />}>
              <Route path="/biblioteca" element={<Biblioteca />} />
            </Route>

            {/* Assistente IA: admin, exxer (sem gestao_escolar, sem dados escolares), coordenador e
                professor (só leitura e relatórios, só as turmas em que leciona; a API confere).
                /busca-ia era o endereço antigo (favoritos e links): só redireciona. */}
            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador', 'professor']} />}>
              <Route path="/assistente" element={<Assistente />} />
            </Route>
            <Route path="/busca-ia" element={<Navigate to="/assistente" replace />} />

            {/* Tarefas: admin e exxer com a flag "tarefas"; a API confere a flag. */}
            <Route element={<RoleRoute allowed={['admin', 'exxer']} />}>
              <Route path="/tarefas" element={<TarefasLayout />}>
                <Route index element={<MinhasTarefas />} />
                <Route path="projetos" element={<TarefasProjetos />} />
                <Route path="projetos/:id" element={<TarefasProjetoDetalhe />} />
                <Route path="cronograma" element={<TarefasCronograma />} />
                <Route path="relatorio" element={<TarefasRelatorio />} />
                <Route path="mural" element={<TarefasMural />} />
                <Route path="equipes" element={<TarefasEquipes />} />
                <Route path="jornadas" element={<TarefasJornadas />} />
                <Route path="ferias" element={<TarefasFerias />} />
              </Route>
            </Route>

            {/* Assinaturas (drive público): admin e exxer com a flag "assinaturas"; a API confere a flag. */}
            <Route element={<RoleRoute allowed={['admin', 'exxer']} assinaturas />}>
              <Route path="/assinaturas" element={<Assinaturas />} />
            </Route>

            {/* Prestação de Contas: o acesso vem do /me (prestacao_contas), calculado pelo backend com
                o interruptor PRESTACAO_CONTAS_LIBERADA (0: só o admin; financeiro: + flag financeiro; 1: todo exxer). A API confere de novo. */}
            <Route element={<RoleRoute allowed={['admin', 'exxer']} prestacao="colaborador" />}>
              <Route path="/prestacao-contas" element={<MinhasPrestacoes />} />
              <Route path="/prestacao-contas/:id" element={<PrestacaoDetalhe />} />
            </Route>
            <Route element={<RoleRoute allowed={['admin', 'exxer']} prestacao="financeiro" />}>
              <Route path="/financeiro/prestacao-contas" element={<PainelFinanceiro />} />
              <Route path="/financeiro/prestacao-contas/importacoes" element={<FinImportacoes />} />
              <Route path="/financeiro/prestacao-contas/importacoes/:id" element={<FinImportacaoDetalhe />} />
              <Route path="/financeiro/prestacao-contas/prestacoes/:id" element={<FinRevisaoPrestacao />} />
              <Route path="/financeiro/prestacao-contas/uber" element={<FinUberCorporativo />} />
            </Route>

            {/* Andon: admin e exxer com usuarios.andon ('tv' é redirecionado para /andon/tv). */}
            <Route element={<AndonRoute />}>
              <Route path="/andon" element={<AndonPainel />} />
            </Route>

            {/* Licenças: admin e exxer com as flags de gestão escolar e licenças, e o
                coordenador (só vê e copia). A página e a API conferem a flag. */}
            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador']} gestaoEscolar />}>
              <Route path="/licencas" element={<Licencas />} />
            </Route>

            <Route element={<RoleRoute allowed={['admin', 'exxer', 'coordenador', 'professor', 'aluno']} moduleKey="cloud" />}>
              <Route path="/cloud" element={<CloudMqtt />} />
            </Route>

            {/* Admin: o Painel Admin do exxer depende da flag de gestão escolar. */}
            <Route element={<RoleRoute allowed={['admin', 'exxer']} gestaoEscolar />}>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/escolas" element={<CadastroEscola />} />
              <Route path="/admin/coordenadores" element={<CadastroCoordenador />} />
              <Route path="/admin/alunos-escolas" element={<VincularAlunosEscolas />} />
              <Route path="/admin/cadastro-lote" element={<CadastroLote />} />
            </Route>
            <Route element={<RoleRoute allowed={['admin', 'exxer']} moduleKey="cursos" gestaoEscolar />}>
              <Route path="/admin/cursos" element={<Cursos gerenciarProfessor />} />
            </Route>
            <Route element={<RoleRoute allowed={['admin', 'exxer']} />}>
              <Route path="/admin/biblioteca" element={<BibliotecaAdmin />} />
            </Route>

            <Route element={<RoleRoute allowed={['admin']} />}>
              <Route path="/admin/exxer" element={<CadastroExxer />} />
              <Route path="/admin/temporario" element={<CadastroTemporario />} />
              <Route path="/admin/informacoes" element={<InformacoesAdmin />} />
              <Route path="/admin/auditoria" element={<Auditoria />} />
              <Route path="/admin/ia" element={<ConsumoIA />} />
              <Route path="/monitorar/usuarios-online" element={<UsuariosOnline />} />
            </Route>

            {/* Coordenador */}
            <Route element={<RoleRoute allowed={['coordenador', 'admin', 'exxer']} gestaoEscolar />}>
              <Route path="/coordenador/escolas" element={<CadastroEscola />} />
            </Route>
            <Route element={<RoleRoute allowed={['coordenador', 'admin', 'exxer']} moduleKey={['cursos', 'cloud', 'biblioteca']} gestaoEscolar />}>
              <Route path="/coordenador/professores" element={<CadastroProfessor />} />
              <Route path="/coordenador/alunos" element={<CadastroAluno />} />
            </Route>
            <Route element={<RoleRoute allowed={['coordenador', 'admin', 'exxer']} moduleKey="cursos" gestaoEscolar />}>
              <Route path="/coordenador/turmas" element={<ExigeCoordenador><CadastroTurma /></ExigeCoordenador>} />
            </Route>
            <Route element={<RoleRoute allowed={['coordenador', 'admin', 'exxer']} moduleKey="cursos" gestaoEscolar />}>
              <Route path="/coordenador/vincular-alunos" element={<ExigeCoordenador><VincularAlunos /></ExigeCoordenador>} />
              <Route path="/coordenador/vincular-professores" element={<ExigeCoordenador><VincularProfessores /></ExigeCoordenador>} />
            </Route>
            {/* Provas do Simmaq das turmas e escolas da coordenação (ver, encerrar, exportar, excluir). */}
            <Route element={<RoleRoute allowed={['coordenador', 'admin', 'exxer']} gestaoEscolar />}>
              <Route path="/coordenador/provas-simmaq" element={<ExigeCoordenador><ListaProvasSimmaq /></ExigeCoordenador>} />
              <Route path="/coordenador/provas-simmaq/:id" element={<ExigeCoordenador><ProvaSimmaq /></ExigeCoordenador>} />
              <Route path="/coordenador/provas-simmaq/:id/tentativas/:tid" element={<ExigeCoordenador><TentativaSimmaq /></ExigeCoordenador>} />
            </Route>
            {/* Calendario, alocacao e avisos sao so do coordenador: o admin nao
                usa estas telas em nome de um coordenador. */}
            <Route element={<RoleRoute allowed={['coordenador']} moduleKey="cursos" />}>
              <Route path="/coordenador/calendario" element={<CalendarioTurmas />} />
              <Route path="/coordenador/alocacao" element={<Alocacao />} />
              <Route path="/coordenador/avisos" element={<Avisos />} />
            </Route>

            {/* Professor */}
            <Route element={<RoleRoute allowed={['professor']} moduleKey="cursos" />}>
              <Route path="/professor/cursos" element={<ListarCursos />} />
              <Route path="/professor/curso/novo" element={<CadastroCurso />} />
              <Route path="/professor/curso/:id/editar" element={<CadastroCurso />} />
            </Route>
            <Route element={<RoleRoute allowed={['professor']} moduleKey="cursos" />}>
              <Route path="/professor/calendario" element={<CalendarioTurmas />} />
            </Route>
            <Route element={<RoleRoute allowed={['professor']} moduleKey="cursos" />}>
              <Route path="/professor/provas" element={<ProvasProfessor />} />
              <Route path="/professor/vincular-provas" element={<VincularProvas />} />
              <Route path="/professor/avaliar-provas" element={<AvaliarProvas />} />
              <Route path="/avaliar/:provaId/:alunoId" element={<AvaliarAluno />} />
            </Route>
            <Route element={<RoleRoute allowed={['professor']} moduleKey="cursos" />}>
              <Route path="/professor/presenca" element={<Presenca />} />
            </Route>
            {/* Provas online do Simmaq 3DX: sem módulo de escola (o professor usa o simulador,
                não o LMS). A API confere a turma e o dono da prova. */}
            <Route element={<RoleRoute allowed={['professor']} />}>
              <Route path="/professor/provas-simmaq" element={<ListaProvasSimmaq />} />
              <Route path="/professor/provas-simmaq/nova" element={<FormProvaSimmaq />} />
              <Route path="/professor/provas-simmaq/:id" element={<ProvaSimmaq />} />
              <Route path="/professor/provas-simmaq/:id/editar" element={<FormProvaSimmaq />} />
              <Route path="/professor/provas-simmaq/:id/tentativas/:tid" element={<TentativaSimmaq />} />
            </Route>

            {/* Aluno */}
            <Route element={<RoleRoute allowed={['aluno']} moduleKey="cursos" />}>
              <Route path="/aluno/meus-cursos" element={<MeusCursosAluno />} />
              <Route path="/aluno/provas" element={<ProvasAluno />} />
              <Route path="/aluno/resultado-provas" element={<ResultadoProvasAlunos />} />
            </Route>
            <Route element={<RoleRoute allowed={['aluno']} moduleKey="cursos" />}>
              <Route path="/aluno/calendario" element={<CalendarioTurmas />} />
            </Route>
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={usuario ? (ehAndonTv(usuario) ? ROTA_ANDON_TV : '/home') : '/login'} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
